import React, { useCallback, useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  DeleteForever as DeleteForeverIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Switch } from "ctech-react-components";

import Input from "components/Input";

import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import { useAppDispatch } from "store/hooks";
import {
  handleUpdateWorkingAreaRequest,
  handleDeleteWorkingAreaRequest,
  handleUpdateWorkingAreaStatusRequest,
} from "store/modules/Logistics/thunk.store";

import * as Styled from "./styles";

interface NameStatusData {
  name: string;
  isActive: boolean;
}

interface NameStatusCardProps {
  workingAreaData: CoverageAreaProps;
  goBack: () => void;
  dealerId: string;
}

const NameStatusCard = ({
  workingAreaData,
  dealerId,
  goBack,
}: NameStatusCardProps) => {
  const [editMode, setEditMode] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<NameStatusData>({
    defaultValues: {
      name: workingAreaData.name,
      isActive: !!workingAreaData.enabled,
    },
  });

  const isActive = watch("isActive");

  const syncFormWithWorkingArea = useCallback(() => {
    reset({
      name: workingAreaData.name,
      isActive: !!workingAreaData.enabled,
    });
  }, [reset, workingAreaData.enabled, workingAreaData.name]);

  const handleChangeEditMode = () => {
    if (editMode) {
      syncFormWithWorkingArea();
    }
    setEditMode((state) => !state);
  };

  const handleRemoveWorkingArea = async () => {
    await dispatch(
      handleDeleteWorkingAreaRequest({
        id: workingAreaData.id,
        dealerId,
      }),
    ).unwrap();
    goBack();
  };

  const handleEditWorkingArea = async (data: NameStatusData) => {
    await dispatch(
      handleUpdateWorkingAreaRequest({
        ...workingAreaData,
        ...data,
        dealerId,
      }),
    ).unwrap();

    handleChangeEditMode();
  };

  useEffect(() => {
    setEditMode(false);
    syncFormWithWorkingArea();
  }, [workingAreaData.id, syncFormWithWorkingArea]);

  return (
    <Styled.Container onSubmit={handleSubmit(handleEditWorkingArea)}>
      <Styled.TopContainer>
        <Typography variant="caption">{t("nameAndStatus")}</Typography>

        <Styled.SwitchContainer editMode isActive={isActive}>
          <Styled.IsActiveText isActive={isActive}>
            {isActive ? t("active") : t("inactive")}
          </Styled.IsActiveText>

          <Switch
            key={workingAreaData.id}
            name="isActive"
            control={control}
            setValue={setValue}
            size="small"
            color="success"
            onChange={async (_, enabled) => {
              await dispatch(
                handleUpdateWorkingAreaStatusRequest({
                  enabled,
                  id: workingAreaData.id,
                  dealerId,
                }),
              ).unwrap();
              setValue("isActive", enabled);
            }}
          />
        </Styled.SwitchContainer>
      </Styled.TopContainer>
      {editMode ? (
        <Input
          name="name"
          error={errors.name}
          label={t("areaName")}
          defaultValue={workingAreaData.name}
          register={register}
          inputProps={{ maxLength: 40 }}
          fullWidth
          registerOptions={{
            required: t("requiredField"),
            minLength: { message: t("threeCharacteresMin"), value: 3 },
          }}
        />
      ) : (
        <Typography>{workingAreaData?.name}</Typography>
      )}

      <Styled.ActionsContainer editMode={editMode}>
        {editMode ? (
          <>
            <Styled.ActionContainer
              endIcon={<DeleteForeverIcon />}
              onClick={handleRemoveWorkingArea}
            >
              {t("delete")}
            </Styled.ActionContainer>
            <Styled.AlignEndContainer>
              <Styled.ActionContainer
                endIcon={<CloseIcon />}
                color="inherit"
                onClick={handleChangeEditMode}
              >
                {t("close")}
              </Styled.ActionContainer>
              <Styled.ActionContainer
                endIcon={<SaveIcon />}
                color="secondary"
                type="submit"
              >
                {t("save")}
              </Styled.ActionContainer>
            </Styled.AlignEndContainer>
          </>
        ) : (
          <Styled.ActionContainer
            color="secondary"
            onClick={handleChangeEditMode}
            endIcon={<EditIcon />}
          >
            {t("edit")}
          </Styled.ActionContainer>
        )}
      </Styled.ActionsContainer>
    </Styled.Container>
  );
};

export default NameStatusCard;
